import { Head as Helmet } from 'vite-react-ssg';
import { seoConfig, PageSEO } from '@/data/seo-config';
import { truncateOnWord } from '@/utils/truncateOnWord';

interface SEOProps {
    page?: keyof typeof seoConfig; // Key from our config
    // Allow overriding specific fields if needed (e.g. for dynamic pages using this component as a base)
    override?: Partial<PageSEO>;
    // Allow passing raw data for dynamic pages that don't have a config key
    dynamicData?: PageSEO;
}

export const SEO = ({ page, override, dynamicData }: SEOProps) => {
    // 1. Try to get data from config
    let data = page ? seoConfig[page] : undefined;

    // 2. If dynamic data is provided, use that instead (or merge)
    if (dynamicData) {
        data = dynamicData;
    }

    // 3. Apply overrides
    if (data && override) {
        data = { ...data, ...override };
    }

    // 3.5 Enforce trailing slash on canonicals
    if (data && data.canonical && !data.canonical.endsWith('/')) {
        data.canonical = `${data.canonical}/`;
    }

    // 4. Fallback or return null if no data
    if (!data) {
        return null;
    }

    // Safety net only — titles should be authored under ~60 chars so this never
    // fires. When it does, cut on a word boundary: a hard `.slice(0, 67)` used to
    // publish titles that ended mid-word, which is worse in a SERP than a title
    // Google truncates itself.
    const safeTitle = truncateOnWord(data.title, 70);

    return (
        <Helmet>
            <title>{safeTitle}</title>
            <meta name="description" content={data.description} />
            {data.keywords && <meta name="keywords" content={data.keywords} />}
            {data.canonical && <link rel="canonical" href={data.canonical} />}
            <meta name="robots" content={data.noindex ? "noindex,nofollow" : "index,follow"} />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Ortem Technologies" />
            {data.canonical && <meta property="og:url" content={data.canonical} />}
            <meta property="og:title" content={data.title} />
            <meta property="og:description" content={data.description} />
            {data.ogImage && <meta property="og:image" content={data.ogImage} />}
            {data.ogImage && <meta property="og:image:width" content="1200" />}
            {data.ogImage && <meta property="og:image:height" content="630" />}
            <meta property="og:locale" content="en_US" />

            {/* Twitter / X Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@OrtemT" />
            {data.canonical && <meta name="twitter:url" content={data.canonical} />}
            <meta name="twitter:title" content={data.title} />
            <meta name="twitter:description" content={data.description} />
            {data.ogImage && <meta name="twitter:image" content={data.ogImage} />}
        </Helmet>
    );
};
